import { useTranslations } from 'next-intl'

import type { SanityClient } from '@sanity/client'

import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import type { IArtistListItem } from '~/components/blocks/ArtistsList/hooks'
import ArtistsContainer from '~/containers/ArtistsContainer/ArtistsContainer'
import { BannerTheme } from '~/enums/banner-theme'
import {
  advantagesBanner,
  image,
  internationalizedTextField,
} from '~/schemas/common'
import { onlyPublished } from '~/services/sanity'
import { PushInteractionLocation } from '~/utils/posthogEvents'

import type { PageTemplateModule, TemplateParams } from './types'

interface ArtistsTemplateData {
  pageSettings: {
    advantagesBanner: AdvantagesBannerProps
    artistsPageSummary: string
    popularArtists: any[]
    popularFeaturedArtists: any[]
  }
  fallbackData: IArtistListItem[]
}

/**
 * Artists Template Component
 * Renders the artists index page with searchable artist list
 */
function ArtistsTemplate({ data }: { data: any }) {
  const { pageSettings, fallbackData } = data
  const tNavigation = useTranslations('navigation')

  const featuredArtists =
    pageSettings?.popularFeaturedArtists?.map((artist: any) => {
      return {
        ...artist,
        featured: true,
      }
    }) || []
  const popularArtists =
    pageSettings?.popularArtists?.map((artist: any) => {
      return {
        ...artist,
        featured: false,
      }
    }) || []

  return (
    <>
      <PageHeader
        title={tNavigation('artists')}
        preventSplitLines
        summary={pageSettings.artistsPageSummary || undefined}
      />

      <ArtistsContainer
        fallbackData={fallbackData}
        popularArtistsList={[...featuredArtists, ...popularArtists]}
      />
      <AdvantagesBanner
        {...pageSettings.advantagesBanner}
        theme={BannerTheme.offwhite}
        analyticsInteractionLocation={PushInteractionLocation.ArtistsPage}
        analyticsCtaSource="ArtistsPage"
      />
    </>
  )
}

/**
 * Artists Page Template Module
 * Fetches settings data with popular artists and fallback artist list
 */
export const artistsTemplate: PageTemplateModule<ArtistsTemplateData> = {
  async fetchData(client: SanityClient, params: TemplateParams) {
    const { lang } = params

    const data = await client.fetch<ArtistsTemplateData>(
      `{
        "pageSettings": *[_type == 'settings'][0] {
          "artistsPageSummary": ${internationalizedTextField('artistsPageSummary')},
          ${advantagesBanner},
          "popularArtists": artistsPagePopularArtists[]-> {
            _id,
            "slug": slug.current,
            name,
            ${image},
            totalArtworks
          },
          "popularFeaturedArtists": artistsPagePopularFeaturedArtists[]-> {
            _id,
            "slug": slug.current,
            name,
            ${image},
            totalArtworks
          },
        },
        "fallbackData": *[_type == 'artist' ${onlyPublished}] | order(name asc) {
          _id,
          name,
          "qualifiedUrl": slug.current,
          totalArtworks,
          tierLevel
        }
      }`,
      { lang }
    )

    return data
  },

  component: ArtistsTemplate,
}
